import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AxiosApiCall from "../api/AxiosApiCall";
import API_URLS from "../api/ApiServices";
import { toast } from "react-toastify";

const axiosInstance = AxiosApiCall();

const STALE_5MIN = 1000 * 60 * 5;
const STALE_30MIN = 1000 * 60 * 30;

// ====================== SUPPLY VENDORS ======================

export const useSupplyVendors = (filters = {}) => {
  const { search = "", status = "", per_page = "", page = "" } = filters;
  return useQuery({
    queryKey: ["supplyVendors", search, status, per_page, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (status) params.append("status", status);
      if (per_page) params.append("per_page", per_page);
      if (page) params.append("page", page);
      const url = `${API_URLS.getSupplyVendors}${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await axiosInstance.get(url);
      return res.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const useSupplyVendorsDropdown = () => {
  return useQuery({
    queryKey: ["supplyVendorsDropdown"],
    queryFn: async () => {
      const res = await axiosInstance.get(API_URLS.getSupplyVendorsDropdown);
      return res.data;
    },
    staleTime: STALE_30MIN,
  });
};

export const useSupplyVendorById = (vendorId) => {
  return useQuery({
    queryKey: ["supplyVendor", vendorId],
    queryFn: async () => {
      const res = await axiosInstance.get(`${API_URLS.getSupplyVendorById}?vendor_id=${vendorId}`);
      return res.data;
    },
    enabled: !!vendorId,
    staleTime: STALE_5MIN,
  });
};

export const useCreateSupplyVendor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const res = await axiosInstance.post(API_URLS.createSupplyVendor, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplyVendors"] });
      queryClient.invalidateQueries({ queryKey: ["supplyVendorsDropdown"] });
      toast.success("Supply vendor created successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to create supply vendor");
    },
  });
};

export const useUpdateSupplyVendor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const res = await axiosInstance.put(API_URLS.updateSupplyVendor, data);
      return res.data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["supplyVendors"] });
      queryClient.invalidateQueries({ queryKey: ["supplyVendor", vars.vendor_id] });
      queryClient.invalidateQueries({ queryKey: ["supplyVendorsDropdown"] });
      toast.success("Supply vendor updated successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to update supply vendor");
    },
  });
};

export const useDeleteSupplyVendor = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vendorId) => {
      const res = await axiosInstance.delete(API_URLS.deleteSupplyVendor, {
        data: { vendor_id: vendorId },
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplyVendors"] });
      queryClient.invalidateQueries({ queryKey: ["supplyVendorsDropdown"] });
      toast.success("Supply vendor deleted successfully!");
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to delete supply vendor";
      toast.error(errorMessage);
    },
  });
};

// ====================== BANK ACCOUNTS ======================

export const useBankAccounts = (filters = {}) => {
  const { search = "", status = "", per_page = "" } = filters;
  return useQuery({
    queryKey: ["bankAccounts", search, status, per_page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (status) params.append("status", status);
      if (per_page) params.append("per_page", per_page);
      const url = `${API_URLS.getBankAccounts}${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await axiosInstance.get(url);
      return res.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const useBankAccountsDropdown = () => {
  return useQuery({
    queryKey: ["bankAccountsDropdown"],
    queryFn: async () => {
      const res = await axiosInstance.get(API_URLS.getBankAccountsDropdown);
      return res.data;
    },
    staleTime: STALE_30MIN,
  });
};

export const useBankAccountById = (accountId) => {
  return useQuery({
    queryKey: ["bankAccount", accountId],
    queryFn: async () => {
      const res = await axiosInstance.get(`${API_URLS.getBankAccountById}?bank_account_id=${accountId}`);
      return res.data;
    },
    enabled: !!accountId,
    staleTime: STALE_5MIN,
  });
};

export const useCreateBankAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const res = await axiosInstance.post(API_URLS.createBankAccount, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bankAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["bankAccountsDropdown"] });
      toast.success("Bank account created successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to create bank account");
    },
  });
};

export const useUpdateBankAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const res = await axiosInstance.put(API_URLS.updateBankAccount, data);
      return res.data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["bankAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["bankAccount", vars.bank_account_id] });
      queryClient.invalidateQueries({ queryKey: ["bankAccountsDropdown"] });
      toast.success("Bank account updated successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to update bank account");
    },
  });
};

export const useDeleteBankAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (accountId) => {
      const res = await axiosInstance.delete(API_URLS.deleteBankAccount, {
        data: { bank_account_id: accountId },
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["bankAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["bankAccountsDropdown"] });
      toast.success("Bank account deleted successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete bank account");
    },
  });
};

// ====================== VENDOR PAYMENTS ======================

export const useVendorPayments = (filters = {}) => {
  const {
    search = "",
    supply_vendor_id = "",
    company_bank_account_id = "",
    start_date = "",
    end_date = "",
    per_page = "",
    page = "",
  } = filters;
  return useQuery({
    queryKey: ["vendorPayments", search, supply_vendor_id, company_bank_account_id, start_date, end_date, per_page, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (supply_vendor_id) params.append("supply_vendor_id", supply_vendor_id);
      if (company_bank_account_id) params.append("company_bank_account_id", company_bank_account_id);
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      if (per_page) params.append("per_page", per_page);
      if (page) params.append("page", page);
      const url = `${API_URLS.getVendorPayments}${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await axiosInstance.get(url);
      return res.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const useVendorPaymentById = (paymentId) => {
  return useQuery({
    queryKey: ["vendorPayment", paymentId],
    queryFn: async () => {
      const res = await axiosInstance.get(`${API_URLS.getVendorPaymentById}?payment_id=${paymentId}`);
      return res.data;
    },
    enabled: !!paymentId,
    staleTime: STALE_5MIN,
  });
};

export const useCreateVendorPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const res = await axiosInstance.post(API_URLS.createVendorPayment, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendorPayments"] });
      toast.success("Payment recorded successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to record payment");
    },
  });
};

export const useUpdateVendorPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const res = await axiosInstance.put(API_URLS.updateVendorPayment, data);
      return res.data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["vendorPayments"] });
      queryClient.invalidateQueries({ queryKey: ["vendorPayment", vars.payment_id] });
      toast.success("Payment updated successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to update payment");
    },
  });
};

export const useDeleteVendorPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (paymentId) => {
      const res = await axiosInstance.delete(API_URLS.deleteVendorPayment, {
        data: { payment_id: paymentId },
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vendorPayments"] });
      toast.success("Payment deleted successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete payment");
    },
  });
};

export const useAdminPurchasesDropdown = () => {
  return useQuery({
    queryKey: ["adminPurchasesDropdown"],
    queryFn: async () => {
      const res = await axiosInstance.get(API_URLS.getAdminPurchasesDropdown);
      return res.data;
    },
    staleTime: STALE_30MIN,
  });
};

export const useAdminPurchases = (filters = {}) => {
  const {
    search = "",
    supply_vendor_id = "",
    status = "",
    payment_status = "",
    start_date = "",
    end_date = "",
    per_page = "",
    page = "",
  } = filters;
  return useQuery({
    queryKey: ["adminPurchases", search, supply_vendor_id, status, payment_status, start_date, end_date, per_page, page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (supply_vendor_id) params.append("supply_vendor_id", supply_vendor_id);
      if (status) params.append("status", status);
      if (payment_status) params.append("payment_status", payment_status);
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      if (per_page) params.append("per_page", per_page);
      if (page) params.append("page", page);
      const url = `${API_URLS.getAdminPurchases}${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await axiosInstance.get(url);
      return res.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const useAdminPurchaseById = (purchaseId) => {
  return useQuery({
    queryKey: ["adminPurchase", purchaseId],
    queryFn: async () => {
      const res = await axiosInstance.get(`${API_URLS.getAdminPurchaseById}?purchase_id=${purchaseId}`);
      return res.data;
    },
    enabled: !!purchaseId,
    staleTime: STALE_5MIN,
  });
};

export const useCreateAdminPurchase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data) => {
      const res = await axiosInstance.post(API_URLS.createAdminPurchase, data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminPurchases"] });
      toast.success("Purchase order created successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to create purchase order");
    },
  });
};

export const useApproveAdminPurchase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (purchaseId) => {
      const res = await axiosInstance.post(API_URLS.approveAdminPurchase, { purchase_id: purchaseId });
      return res.data;
    },
    onSuccess: (_, purchaseId) => {
      queryClient.invalidateQueries({ queryKey: ["adminPurchases"] });
      queryClient.invalidateQueries({ queryKey: ["adminPurchase", purchaseId] });
      queryClient.invalidateQueries({ queryKey: ["adminStock"] });
      toast.success("Purchase approved and inventory updated!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to approve purchase");
    },
  });
};

export const useCancelAdminPurchase = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ purchaseId, reason }) => {
      const res = await axiosInstance.post(API_URLS.cancelAdminPurchase, { purchase_id: purchaseId, reason });
      return res.data;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ["adminPurchases"] });
      queryClient.invalidateQueries({ queryKey: ["adminPurchase", vars.purchaseId] });
      toast.success("Purchase cancelled successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to cancel purchase");
    },
  });
};

// ====================== ADMIN STOCK ======================

export const useAdminStock = (filters = {}) => {
  const { search = "", low_stock = "", out_of_stock = "", per_page = "" } = filters;
  return useQuery({
    queryKey: ["adminStock", search, low_stock, out_of_stock, per_page],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (low_stock) params.append("low_stock", low_stock);
      if (out_of_stock) params.append("out_of_stock", out_of_stock);
      if (per_page) params.append("per_page", per_page);
      const url = `${API_URLS.getAdminStock}${params.toString() ? `?${params.toString()}` : ""}`;
      const res = await axiosInstance.get(url);
      return res.data;
    },
    staleTime: STALE_5MIN,
  });
};

// ====================== PRODUCTS ======================

export const useProducts = () => {
  return useQuery({
    queryKey: ["products"],
    queryFn: async () => {
      const res = await axiosInstance.get(API_URLS.getProducts);
      return res.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (productId) => {
      const res = await axiosInstance.delete(API_URLS.removeProducts, {
        data: { product_id: productId },
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      toast.success("Product deleted successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete product");
    },
  });
};

// ====================== PRODUCT STOCKS ======================

export const useProductStocks = () => {
  return useQuery({
    queryKey: ["productStocks"],
    queryFn: async () => {
      const res = await axiosInstance.get(API_URLS.getAllProductsStock);
      return res.data;
    },
    staleTime: STALE_5MIN,
  });
};

export const useDeleteProductStock = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (stockId) => {
      const res = await axiosInstance.delete(API_URLS.removeProductStock, {
        data: { stock_id: stockId },
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["productStocks"] });
      toast.success("Stock deleted successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete stock");
    },
  });
};

export const useDeleteFlashSale = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (flashSaleId) => {
      const res = await axiosInstance.delete(API_URLS.removeFlashsales, {
        data: { flash_sale_id: flashSaleId },
      });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["productStocks"] });
      toast.success("Flash sale deleted successfully!");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || error?.message || "Failed to delete flash sale");
    },
  });
};

// ====================== VENDOR RECONCILIATION ======================

export const useVendorReconciliation = (vendorId, filters = {}) => {
  const { start_date = "", end_date = "" } = filters;
  return useQuery({
    queryKey: ["vendorReconciliation", vendorId, start_date, end_date],
    queryFn: async () => {
      const params = new URLSearchParams({ vendor_id: vendorId });
      if (start_date) params.append("start_date", start_date);
      if (end_date) params.append("end_date", end_date);
      const res = await axiosInstance.get(`${API_URLS.getVendorReconciliation}?${params.toString()}`);
      return res.data;
    },
    enabled: !!vendorId,
    staleTime: STALE_5MIN,
  });
};
